import { useState } from 'react';
import { uploadFile, deleteFile } from '@/shared/db';
import type { UseFileUploadOptions } from '../shared/types';

export function useFileUpload(options: UseFileUploadOptions) {
  const { bucket, folder, onSuccess, onError } = options;
  
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [uploadedPath, setUploadedPath] = useState<string | null>(null);

  const upload = async (file: File) => {
    try {
      setUploading(true);
      setError(null);
      setProgress(0);

      // Simulate progress (since Supabase doesn't provide upload progress)
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const result = await uploadFile({
        bucket,
        folder,
        file,
      });

      clearInterval(progressInterval);
      setProgress(100);
      setUploadedUrl(result.url);
      setUploadedPath(result.path);

      if (onSuccess) {
        onSuccess(result.url, result.path);
      }

      return result;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      if (onError) {
        onError(error);
      }
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setUploading(false);
    setProgress(0);
    setError(null);
    setUploadedUrl(null);
    setUploadedPath(null);
  };

  return {
    upload,
    uploading,
    progress,
    error,
    uploadedUrl,
    uploadedPath,
    reset,
  };
}

export function useFileDelete() {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteFileHandler = async (bucket: string, path: string) => {
    try {
      setDeleting(true);
      setError(null);

      await deleteFile({ bucket, path });
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw error;
    } finally {
      setDeleting(false);
    }
  };

  return {
    deleteFile: deleteFileHandler,
    deleting,
    error,
  };
}

